"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Globe as Globe2, Building2, ArrowUpRight, CircleCheck as CheckCircle2, Compass } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const directories = [{
  title: "Local SEO",
  to: "/LocalSEOPage",
  badge: "Regional Visibility",
  icon: MapPin,
  description: "Capture high-intent search queries in your immediate service areas. From Google Business Profile optimization to hyper-local landing pages.",
  deliverables: ["Google Business Profile optimization", "Local citation audit & cleanup", "Geo-targeted landing page structure"]
}, {
  title: "National SEO",
  to: "/NationalSeoPage",
  badge: "UK-Wide Authority",
  icon: Globe2,
  description: "Expand your search presence across the entire country. Compete for high-volume commercial keywords with thorough technical and content strategy.",
  deliverables: ["Competitive keyword mapping", "Full-funnel commercial content plan", "Sustainable high-authority link acquisition"]
}, {
  title: "UK Cities & Areas hub",
  to: "/uk-cities-areas",
  badge: "Location Directory",
  icon: Building2,
  description: "Browse dedicated local strategy guides and coverage across key English, Scottish, Welsh, and Northern Irish business hubs.",
  deliverables: ["Coverage for 30+ regional UK business hubs", "Localized intent benchmarks", "Direct consultancy across every region"]
}];
export default function Directory() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicelocationsdirectory" data-nav="light" className="bg-background py-20 md:py-28 border-b border-border transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium">
              <Compass className="size-3.5 text-primary" />
              <span>Targeted search coverage</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Search strategy tailored to your target territory
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Whether you need to improve search visibility in a single town, across multiple UK regions, or nationwide, I build and execute the roadmap myself.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild size="lg" className="rounded-lg shadow-sm font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowUpRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-40px"
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {directories.map((dir, i) => {
          const IconComponent = dir.icon;
          return <motion.div key={dir.title} variants={itemVariants} data-index={i}>
                <Card className="h-full flex flex-col justify-between border border-border bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary w-fit">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="border-border text-muted-foreground font-normal">
                        {dir.badge}
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      <CardTitle className="text-2xl font-bold text-card-foreground font-default">
                        <Link to={dir.to} className="inline-flex items-center gap-1.5 hover:text-primary transition-colors group">
                          <span>{dir.title}</span>
                          <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-primary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                      </CardTitle>
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {dir.description}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6 pt-2">
                    <div className="space-y-2.5 pt-4 border-t border-border/70">
                      {dir.deliverables.map((item, dIndex) => <div key={dIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/80">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </div>)}
                    </div>

                    <div className="pt-2">
                      <Button asChild variant="link" className="p-0 h-auto font-medium text-primary hover:text-primary/80 group inline-flex items-center gap-1">
                        <Link to={dir.to}>
                          Explore {dir.title}
                          <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-14 p-6 sm:p-8 rounded-xl bg-muted/40 border border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-base sm:text-lg font-semibold text-foreground font-default">
              Unsure which geographic scope fits your business model?
            </h3>
            <p className="text-sm text-muted-foreground">
              During an initial discovery call, I assess your target audience and current footprint to recommend the most cost-effective path.
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0 bg-background border-border hover:bg-accent hover:text-accent-foreground">
            <Link to="/ContactPage">
              Speak directly with me
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}